import React, { Suspense, useContext } from 'react';
import { usePortalProps } from './ShadowPortalContext';
import styled from '@emotion/styled';
import { AutoThemeProvider, designTokens } from '@platform-ui/design-system';
import {
  NAV_WIDTH,
  TOGGLE_CHEVRON_POSITION_OPENED,
  TOGGLE_CHEVRON_POSITION_CLOSED,
} from './constants/index';
import { HomepageLink } from './HomepageLink';
import { NavigationContext } from './NavigationContext';
import { RevenueIntegrationSlots } from './RevenueIntegrationSlots';
import { AppBuilderIntegrationSlots } from './AppBuilderIntegrationSlots';
import { ZAppsButton } from './ZAppsButton';
import {
  ChevronLeft,
  MenuOpen as MenuCloseIcon,
  Menu as MenuOpenIcon,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { AppSwitcherData } from './hooks/useAppSwitcherDataTypes';
import { useSupportDeflectionRevenueFlag } from '../nav/flags';
import { GENERIC_VARIANT } from './hooks/useNavigationVariant';
import { GenericIntegrationSlots } from './GenericIntegrationSlots';

const LazyMultiOrgPicker = React.lazy(
  () => import('./MultiOrg/MultiOrgPicker'),
);
const LazyEntityPicker = React.lazy(() => import('./EntityPicker'));
const LazyAppBarSearch = React.lazy(() => import('./AppBarSearch'));
const LazySettingsMenu = React.lazy(() => import('./SettingsMenu'));
const LazySupportDeflection = React.lazy(() => import('../support-deflection'));
const LazyNotificationsButton = React.lazy(
  () => import('./NotificationsButton'),
);

export const APPBAR_HEIGHT = 56;

const Bar = styled.div`
  height: ${APPBAR_HEIGHT}px;
  background-color: ${designTokens.colors.blueNav};
  display: flex;
`;

const LeftBar = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  width: ${NAV_WIDTH};
  flex-shrink: 0;
`;

const RightBar = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  margin-left: auto;
`;

const ToggleWrapper = styled.div<{
  isDrawerOpen: boolean | undefined;
}>`
  position: absolute;
  height: 20px;
  width: 20px;
  top: 100px;
  left: ${({ isDrawerOpen }) =>
    isDrawerOpen
      ? TOGGLE_CHEVRON_POSITION_OPENED
      : TOGGLE_CHEVRON_POSITION_CLOSED};
`;

const ChevronToggle = styled(IconButton)<{
  isDrawerOpen: boolean | undefined;
}>`
  height: 20px;
  width: 20px;
  background-color: white;
  border: 2px solid ${designTokens.colors.blue400};
  z-index: 2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  rotate: ${({ isDrawerOpen }) => !isDrawerOpen && '180deg'};
  transition: all 0.1s ease-in-out;

  svg {
    color: ${designTokens.colors.blue400};
    height: 20px;
  }

  &:hover {
    background-color: ${designTokens.colors.blue400};
    color: white;
    border: 2px solid white;

    svg {
      color: white;
    }
  }
`;

const ChevronDivider = styled.div`
  position: absolute;
  height: 100vh;
  width: 2px;
  left: 21px;
  z-index: -1;
  top: 0;
  background-color: ${designTokens.colors.aqua100};
`;

export const StyledDivider = styled.span`
  display: none;
  margin-left: 16px;

  @media (min-width: 992px) {
    display: block;
    background-color: ${designTokens.colors.darkContextHigh};
    height: 48px;
    width: 1px;
  }
`;
interface StyledIconProps {
  $variant?: string;
}
const getMenuIconColor = (variant?: string, navShow?: boolean) => {
  if (navShow === false) {
    return designTokens.colors.white;
  }
  if (variant === 'zuora') {
    return designTokens.colors.teal300;
  }
  return designTokens.colors.white;
};

export const StyledMenuCloseIcon = styled(MenuCloseIcon)<StyledIconProps>`
  fill: ${({ $variant }) => getMenuIconColor($variant)};
`;

export const StyledMenuOpenIcon = styled(MenuOpenIcon)<StyledIconProps>`
  fill: ${({ $variant }) => getMenuIconColor($variant)};
`;
interface AppBarProps {
  drawerOpen?: boolean;
  toggleDrawer(): void;
  isZASTokenEnabled: boolean;
  appSwitcherData: AppSwitcherData | undefined;
}

export const AppBar = ({
  drawerOpen,
  toggleDrawer,
  isZASTokenEnabled,
  appSwitcherData,
}: AppBarProps) => {
  const { variant } = useContext(NavigationContext);
  const buildPortalProps = usePortalProps();
  const ResolvedIcon = !drawerOpen ? StyledMenuOpenIcon : StyledMenuCloseIcon;
  const isBilling = variant === 'billing';
  const isRevenue = variant === 'revenue';
  const isGeneric = variant === GENERIC_VARIANT;
  const [isRevenueDeflectionEnabled] = useSupportDeflectionRevenueFlag();
  const isTenantHub = window.location.pathname.includes(
    '/platform/apps/tenant-hub',
  );

  return (
    <Bar>
      {isZASTokenEnabled && (
        <>
          <ToggleWrapper isDrawerOpen={drawerOpen}>
            <Tooltip
              title={drawerOpen ? 'Collapse' : 'Expand'}
              placement="right"
              PopperProps={{
                ...buildPortalProps(),
                popperOptions: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                },
              }}
            >
              <ChevronToggle
                isDrawerOpen={drawerOpen}
                onClick={toggleDrawer}
                // WF-9140: Workflow needs this attribute selector
                data-e2e="topnav-drawer-toggle"
              >
                <ChevronLeft />
              </ChevronToggle>
            </Tooltip>
          </ToggleWrapper>
          {!drawerOpen && <ChevronDivider />}
        </>
      )}

      <LeftBar>
        {isZASTokenEnabled ? (
          <AutoThemeProvider disableInjectFirst>
            <ZAppsButton appSwitcherData={appSwitcherData} />
          </AutoThemeProvider>
        ) : (
          <IconButton
            size="large"
            aria-expanded={drawerOpen}
            aria-label="Navigation Menu Panel"
            data-e2e="topnav-drawer-toggle"
            onClick={toggleDrawer}
          >
            <ResolvedIcon
              style={{
                fill: drawerOpen
                  ? designTokens.colors.teal300
                  : designTokens.colors.white,
              }}
            />
          </IconButton>
        )}
        <HomepageLink />
      </LeftBar>
      {isBilling && (
        <Suspense fallback={<React.Fragment />}>
          <LazyAppBarSearch />
        </Suspense>
      )}
      <RightBar>
        {isRevenue && (
          <>
            <RevenueIntegrationSlots />
            {isRevenueDeflectionEnabled && (
              <div
                style={{
                  borderLeft: '1px solid rgba(255, 255, 255, 0.28)',
                  padding: '8px 16px',
                }}
              >
                <Suspense fallback={<React.Fragment />}>
                  <LazySupportDeflection />
                </Suspense>
              </div>
            )}
          </>
        )}
        {isBilling && (
          <>
            {!isZASTokenEnabled && (
              <Suspense fallback={<React.Fragment />}>
                <LazyEntityPicker />
              </Suspense>
            )}
            <Suspense fallback={<React.Fragment />}>
              <LazyMultiOrgPicker />
            </Suspense>
            <Suspense fallback={<React.Fragment />}>
              <LazyNotificationsButton />
            </Suspense>
            <Suspense fallback={<React.Fragment />}>
              <LazySupportDeflection />
            </Suspense>
            <AppBuilderIntegrationSlots />
            <StyledDivider />
            <Suspense fallback={<React.Fragment />}>
              <LazySettingsMenu />
            </Suspense>
          </>
        )}
        {isGeneric && (
          <>
            {isTenantHub && (
              <Suspense fallback={<React.Fragment />}>
                <LazySupportDeflection />
              </Suspense>
            )}
            <GenericIntegrationSlots />
          </>
        )}
      </RightBar>
    </Bar>
  );
};
